import { readApiCode, readApiStatus, readHumanErrorMessage } from "@/shared/utils/apiError";
import {
  NON_SESSION_401_CODES,
  SECURITY_PIN_RESET_REQUIRED_CODE,
  STEP_UP_EMAIL_CODE_INVALID_CODE,
  STEP_UP_EMAIL_COOLDOWN_CODE,
  STEP_UP_EMAIL_NOT_ENABLED_CODE,
  STEP_UP_EMAIL_SENT_CODE,
  STEP_UP_LOCK_ERROR_CODES,
  TWO_FACTOR_CODE_ERROR_CODES,
  TWO_FACTOR_ENROLLMENT_ERROR_CODE,
} from "@/shared/utils/twoFactorCodes";

/**
 * The ways `StepUpService` (coloca-nest) refuses a step-up request, read once and correctly:
 *
 * - 403 `TWO_FACTOR_REQUIRED` — the user has no step-up method (neither the authenticator nor a security PIN).
 *   `HttpApiService` also fires the `auth:two-factor-required` window event on it, which `DashboardLayout`
 *   turns into a redirect to Settings.
 * - 401 `TWO_FACTOR_CODE_REQUIRED` / `TWO_FACTOR_CODE_INVALID` — the header was missing or wrong, for the
 *   authenticator and the PIN alike. The message already says how many attempts are left.
 * - 401 `STEP_UP_LOCKED` — five wrong codes in a row, with either method: 15 minutes.
 * - 401 `SECURITY_PIN_RESET_REQUIRED` — the PIN's tenth wrong attempt. The PIN needs a new one set with the
 *   password, and the server has also closed every session of the user (`useStepUpLockoutWatcher` signs them
 *   out cleanly).
 * - The email backup, asked for with `x-two-factor-code: email` and answered with `email:NNNNNN`: 401
 *   `STEP_UP_EMAIL_SENT` (the code is on its way — the challenge switches to "check your email"), 401
 *   `STEP_UP_EMAIL_COOLDOWN` (one was sent less than a minute ago), 401 `STEP_UP_EMAIL_CODE_INVALID`
 *   (expired, used or out of attempts: ask for another) and 403 `STEP_UP_EMAIL_NOT_ENABLED` (the backup is
 *   off). All of them keep the challenge open.
 *
 * None of these is an expired session, so `HttpApiService` never refreshes and replays them: a replayed wrong
 * code would be counted twice against its lock. Neither is a missing permission either: a challenge modal
 * keeps the person on the same screen and never latches an access denial, which is what `isAccessDeniedError`
 * alone would read them as.
 */
export {
  NON_SESSION_401_CODES,
  SECURITY_PIN_RESET_REQUIRED_CODE,
  STEP_UP_EMAIL_CODE_INVALID_CODE,
  STEP_UP_EMAIL_COOLDOWN_CODE,
  STEP_UP_EMAIL_NOT_ENABLED_CODE,
  STEP_UP_EMAIL_SENT_CODE,
  STEP_UP_LOCK_ERROR_CODES,
  TWO_FACTOR_CODE_ERROR_CODES,
  TWO_FACTOR_ENROLLMENT_ERROR_CODE,
};

/**
 * A refusal the challenge owns — the person stays on it, nothing latches. Includes the 403 of an email code
 * asked for while the backup is off: it is about the challenge, not a missing permission.
 */
export function isTwoFactorCodeError(error: unknown): boolean {
  const code = readApiCode(error);
  const status = readApiStatus(error);
  if (code === null) return false;
  if (status === 403) return code === STEP_UP_EMAIL_NOT_ENABLED_CODE;
  return status === 401 && (TWO_FACTOR_CODE_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * What a challenge's `onSubmit` throws for a step-up refusal: the sentence to show, AND the server's code, so
 * the challenge can tell a wrong code from an email code that is on its way (`STEP_UP_EMAIL_SENT`), which is
 * the opposite of a failure. Throw it with `stepUpRefusal` wherever a refusal is turned into a message.
 */
export class StepUpRefusal extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null) {
    super(message);
    this.name = "StepUpRefusal";
    this.code = code;
  }
}

export function stepUpRefusal(error: unknown, message: string): StepUpRefusal {
  return new StepUpRefusal(message, readApiCode(error));
}

/** The server code behind whatever a challenge's `onSubmit` threw: a `StepUpRefusal`, or a raw API error. */
export function stepUpRefusalCode(error: unknown): string | null {
  if (error instanceof StepUpRefusal) return error.code;
  return readApiCode(error);
}

/** The email backup's code is on its way. */
export function isStepUpEmailSent(error: unknown): boolean {
  return stepUpRefusalCode(error) === STEP_UP_EMAIL_SENT_CODE;
}

/** The longest wait a cooldown can name: the hourly cap (six codes an hour). */
const MAX_EMAIL_COOLDOWN_SECONDS = 3600;

/**
 * How long until another email code may be asked for, read off a `STEP_UP_EMAIL_COOLDOWN` refusal. The
 * backend sends the wait only inside its sentence ("Espera 42 segundos…"), so the first number in it is the
 * answer; null when the refusal is another one or names no number, and the caller falls back to the 60-second
 * rule.
 */
export function stepUpEmailCooldownSeconds(error: unknown): number | null {
  if (stepUpRefusalCode(error) !== STEP_UP_EMAIL_COOLDOWN_CODE) return null;
  const match = /\d+/.exec(readHumanErrorMessage(error, ""));
  if (!match) return null;
  const seconds = Number(match[0]);
  return seconds > 0 ? Math.min(seconds, MAX_EMAIL_COOLDOWN_SECONDS) : null;
}

export function isStepUpLockError(error: unknown): boolean {
  const code = readApiCode(error);
  return (
    readApiStatus(error) === 401 &&
    code !== null &&
    (STEP_UP_LOCK_ERROR_CODES as readonly string[]).includes(code)
  );
}

/** The PIN's hard lock: a new PIN is needed, and every session of the user was closed with it. */
export function isSecurityPinResetRequiredError(error: unknown): boolean {
  return readApiStatus(error) === 401 && readApiCode(error) === SECURITY_PIN_RESET_REQUIRED_CODE;
}

export function isTwoFactorEnrollmentError(error: unknown): boolean {
  return readApiStatus(error) === 403 && readApiCode(error) === TWO_FACTOR_ENROLLMENT_ERROR_CODE;
}

export function isTwoFactorError(error: unknown): boolean {
  return isTwoFactorCodeError(error) || isTwoFactorEnrollmentError(error);
}

/**
 * The sentence a challenge shows for a refused code. Every refusal now carries what the person needs next —
 * how many attempts are left, how long the lock lasts — for the authenticator and the PIN alike, so the
 * backend's sentence (already in their language) is shown; `fallback` covers a refusal without one.
 */
export function twoFactorRefusalMessage(error: unknown, fallback: string): string {
  return readHumanErrorMessage(error, fallback);
}
